import { useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, CalendarCheck, Gift, Cpu } from "lucide-react";
import { Slider } from "./ui/slider";
import { Button } from "./ui/button";

const HERO_IMG = "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHx5b3VuZyUyMGF0aGxldGUlMjBtZW50YWwlMjBmb2N1cyUyMHNvY2NlciUyMHRyYWluaW5nJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzg4NzgwNjc2fDA&ixlib=rb-4.1.0&q=85";

const stateLabel = (score) => {
  if (score >= 80) return { label: "FLOW STATE", desc: "Configurazione ottimale: sei pronto a giocare la tua partita migliore.", color: "text-emerald-400" };
  if (score >= 60) return { label: "STATO COMPETITIVO", desc: "Buona configurazione. Con il coaching mentale puoi salire al livello successivo.", color: "text-sky-400" };
  if (score >= 40) return { label: "STATO INSTABILE", desc: "La mente è in bilico: è qui che il mental coaching fa la differenza.", color: "text-amber-400" };
  return { label: "RESET NECESSARIO", desc: "Lo stato emotivo ti sta sabotando. È il momento perfetto per iniziare un percorso.", color: "text-red-400" };
};

const Param = ({ label, value, onChange, testid, inverted }) => (
  <div>
    <div className="flex justify-between mb-2">
      <span className="text-sm font-medium text-slate-300">{label}</span>
      <span className="text-sm font-bold text-sky-400 font-heading">{inverted ? 100 - value : value}%</span>
    </div>
    <Slider data-testid={testid} value={[value]} onValueChange={([v]) => onChange(v)} max={100} step={1}
      className="[&_[role=slider]]:bg-sky-400 [&_[role=slider]]:border-sky-300 [&_.bg-primary]:bg-sky-400" />
  </div>
);

export const Hero = () => {
  const [focus, setFocus] = useState(65);
  const [ansia, setAnsia] = useState(45);
  const [resilienza, setResilienza] = useState(55);
  const [fiducia, setFiducia] = useState(60);
  const [grinta, setGrinta] = useState(50);
  const score = Math.round((focus + (100 - ansia) + resilienza + fiducia + grinta) / 5);
  const state = stateLabel(score);

  return (
    <section id="top" className="relative min-h-screen flex items-center overflow-hidden grid-overlay">
      <div className="absolute inset-0">
        <img src={HERO_IMG} alt="Giovane atleta in allenamento sotto le luci dello stadio"
             className="w-full h-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090C]/60 via-[#08090C]/80 to-[#08090C]" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-sky-500/10 border border-sky-500/30 text-sky-400 mb-6">
            <Cpu className="w-3.5 h-3.5" /> Mental Coaching Sportivo · Settori Giovanili
          </span>
          <h1 data-testid="hero-slogan-heading"
              className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
            Immagina di avere la capacità di{" "}
            <span className="text-sky-400 text-glow">configurare il tuo stato emotivo</span>{" "}
            per Giocare la tua <span className="text-orange-400" style={{ textShadow: "0 0 40px rgba(251,146,60,0.45)" }}>Partita migliore</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
            Come un'intelligenza artificiale ottimizza i propri parametri per dare il massimo,
            tu puoi allenare la mente a fare lo stesso. Focus, gestione dell'ansia, fiducia:
            non sono doti innate, sono <strong className="text-white">parametri configurabili</strong>.
            E io ti insegno a farlo.
          </p>
          <div className="mt-8 flex flex-wrap gap-4">
            <a href="#prima-sessione" data-testid="hero-cta-free-session">
              <Button size="lg" className="bg-orange-400 text-[#050B14] hover:bg-orange-300 font-semibold shadow-[0_0_30px_rgba(251,146,60,0.35)] hover:shadow-[0_0_40px_rgba(251,146,60,0.55)] transition-shadow">
                <Gift className="w-5 h-5 mr-2" /> Prima Sessione Gratuita
              </Button>
            </a>
            <a href="#prenota" data-testid="hero-cta-booking">
              <Button size="lg" variant="outline" className="border-white/20 text-white hover:bg-white/5 hover:border-sky-500/50 bg-transparent">
                <CalendarCheck className="w-5 h-5 mr-2" /> Prenota Appuntamento
              </Button>
            </a>
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
          <div data-testid="ai-configurator-card"
               className="bg-gradient-to-r from-sky-500/10 via-blue-500/10 to-orange-500/10 border border-sky-500/30 rounded-3xl backdrop-blur-md p-6 sm:p-8 shadow-2xl">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-5 h-5 text-sky-400" />
              <h3 className="text-lg font-semibold text-white font-heading">AI Mindset Configurator</h3>
            </div>
            <p className="text-xs text-slate-400 mb-6">Prova tu stesso: muovi i parametri e osserva come cambia lo stato mentale pre-gara.</p>
            <div className="space-y-5">
              <Param label="Focus" value={focus} onChange={setFocus} testid="ai-slider-focus" />
              <Param label="Controllo Ansia" value={ansia} onChange={setAnsia} testid="ai-slider-ansia" inverted />
              <Param label="Resilienza" value={resilienza} onChange={setResilienza} testid="ai-slider-resilienza" />
              <Param label="Fiducia in sé" value={fiducia} onChange={setFiducia} testid="ai-slider-fiducia" />
              <Param label="Grinta agonistica" value={grinta} onChange={setGrinta} testid="ai-slider-grinta" />
            </div>
            <div className="mt-7 pt-5 border-t border-white/10">
              <div className="flex items-baseline justify-between">
                <span data-testid="ai-state-label" className={`font-heading font-extrabold text-2xl tracking-tight ${state.color}`}>
                  {state.label}
                </span>
                <span data-testid="ai-state-score" className="font-heading font-bold text-3xl text-white">{score}<span className="text-sm text-slate-400">/100</span></span>
              </div>
              <p data-testid="ai-state-desc" className="mt-2 text-sm text-slate-400">{state.desc}</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
